import type { BlueprintPage } from '../types';

/**
 * Stamps each sheet's `overrideScope` — the stable prefix for its own blueprintOverrides keys
 * (header title, footer text). Matches getPageOverrideScope for the first sheet in a scope
 * group so existing keys keep resolving, and only suffixes an ordinal for the 2nd+ sheet
 * sharing that scope (a split kitchen-overview), which would otherwise collide.
 */
export const assignOverrideScopes = (pages: Array<BlueprintPage>): void => {
	const seen = new Map<string, number>();

	pages.forEach((page) => {
		const base = `${page.pageType}.${page.islandId ?? 'kitchen'}`;
		const ordinal = seen.get(base) ?? 0;
		seen.set(base, ordinal + 1);
		page.overrideScope = ordinal === 0 ? base : `${base}.${ordinal}`;
	});
};

export type AssignLegacyPageNumbersParams = {
	pages: Array<BlueprintPage>;
	overviewInsertIndex: number;
	overviewCount: number;
	legacyOverviewCount: number;
};

/**
 * Maps each sheet back to the pageNumber it held under the pre-recalibration overview budget,
 * so overrides saved against the old page-number keys still resolve now that the number of
 * overview sheets can differ. Sheets before the overview block are unmoved; sheets after it
 * shift by the delta; overview sheets past the legacy count have no legacy equivalent.
 */
export const assignLegacyPageNumbers = (params: AssignLegacyPageNumbersParams): void => {
	const { pages, overviewInsertIndex, overviewCount, legacyOverviewCount } = params;
	const delta = overviewCount - legacyOverviewCount;

	pages.forEach((page, i) => {
		if (i < overviewInsertIndex) {
			page.legacyPageNumber = page.pageNumber;
			return;
		}

		if (i < overviewInsertIndex + overviewCount) {
			const ordinal = i - overviewInsertIndex;
			page.legacyPageNumber = ordinal < legacyOverviewCount ? overviewInsertIndex + ordinal + 1 : undefined;
			return;
		}

		page.legacyPageNumber = page.pageNumber - delta;
	});
};
